/**
 * MarketingCalendar — /marketing/calendar
 * Annual Activity Calendar. 12-month × week grid.
 * Mi6 palette: black #0B0B0F · red #DC2626 · gray/white.
 */
import { useState, useEffect, useRef } from "react";
import MarketingShell from "@/components/marketing/MarketingShell";
import {
  ACTIVITY_TYPES,
  loadEvents,
  saveEvents,
  seedEventsIfEmpty,
  generateHooks,
  type MarketingEvent,
} from "@/lib/marketing-hook-generator";
const MI6_BLACK = "#0B0B0F";
const MI6_RED  = "#DC2626";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

// Chip styles per activity type
const CHIP_STYLE: Record<string, { bg: string; text: string; border?: string; pattern?: string }> = {
  "Trade Shows":                              { bg: "#0B0B0F",    text: "#ffffff" },
  "Hosted Round Tables":                      { bg: "#DC2626",    text: "#ffffff" },
  "Webinars / Web Events":                    { bg: "#374151",    text: "#ffffff" },
  "In-Person Hosted Events":                  { bg: "#6B7280",    text: "#ffffff" },
  "Email Campaigns":                          { bg: "transparent", text: "#0B0B0F", border: "#0B0B0F" },
  "Direct Mail Campaigns":                    { bg: "transparent", text: "#4B5563", border: "#6B7280" },
  "Podcast Appearances":                      { bg: "#9CA3AF",    text: "#ffffff" },
  "Sponsored Content":                        { bg: "transparent", text: "#9CA3AF", border: "#9CA3AF" },
  "Paid Ads / Digital Media":                 { bg: "#0B0B0F",    text: "#ffffff", pattern: "hatch" },
  "Co-Marketing Partnerships":                { bg: "transparent", text: "#DC2626", border: "#DC2626" },
  "Speaking Engagements (team as speakers)":  { bg: "#DC2626",    text: "#ffffff" },
  "Other Outreach":                           { bg: "transparent", text: "#374151", border: "#374151" },
};

function statusColor(status: MarketingEvent["status"]) {
  switch (status) {
    case "Completed":   return "#059669";
    case "In Progress": return "#CA8A04";
    case "Cancelled":   return "#9CA3AF";
    default:            return MI6_BLACK;
  }
}

function EventChip({ event, onClick }: { event: MarketingEvent; onClick: () => void }) {
  const style = CHIP_STYLE[event.activityType] || { bg: "#6B7280", text: "#ffffff" };
  return (
    <div
      onClick={onClick}
      className="rounded px-1.5 py-0.5 text-[9px] font-semibold truncate cursor-pointer mb-0.5 leading-tight transition-opacity hover:opacity-80 select-none"
      style={{
        background: style.bg,
        color: style.text,
        border: style.border ? `1px solid ${style.border}` : "none",
        maxWidth: "100%",
      }}
      title={event.name}
    >
      {event.name}
    </div>
  );
}

// Determine which month(s) an event spans
function eventMonths(ev: MarketingEvent): number[] {
  const start = new Date(ev.startDate);
  const end = ev.endDate ? new Date(ev.endDate) : start;
  const months: number[] = [];
  for (let m = start.getMonth(); m <= end.getMonth(); m++) {
    months.push(m);
  }
  return months;
}

const STATUS_OPTIONS = ["Planned","In Progress","Completed","Cancelled"] as const;

function EventModal({
  event,
  onClose,
  onSave,
}: {
  event: Partial<MarketingEvent> | null;
  onClose: () => void;
  onSave: (ev: MarketingEvent) => void;
}) {
  const isEdit = !!event?.id;
  const [form, setForm] = useState<Partial<MarketingEvent>>(
    event || { status: "Planned", activityType: ACTIVITY_TYPES[0] }
  );

  function handle(field: keyof MarketingEvent, value: unknown) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function submit() {
    if (!form.name || !form.startDate || !form.activityType) return;
    const ev: MarketingEvent = {
      id: form.id || `evt-${Date.now()}`,
      name: form.name!,
      activityType: form.activityType!,
      startDate: form.startDate!,
      endDate: form.endDate,
      location: form.location,
      cost: form.cost,
      targetAudience: form.targetAudience,
      status: (form.status as MarketingEvent["status"]) || "Planned",
      actual: form.actual,
      hooks: form.hooks || generateHooks({
        id: form.id || `evt-${Date.now()}`,
        name: form.name!,
        activityType: form.activityType!,
        startDate: form.startDate!,
        status: "Planned",
        location: form.location,
        targetAudience: form.targetAudience,
      }),
    };
    onSave(ev);
  }

  // Esc key dismissal
  useEffect(() => {
    function handleEsc(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="px-6 py-4 flex items-center justify-between" style={{ background: MI6_BLACK }}>
          <h2 className="text-white font-bold text-base">{isEdit ? "Edit Event" : "Add New Event"}</h2>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full text-neutral-300 hover:text-white hover:bg-white/10 transition-colors" aria-label="Close">×</button>
        </div>
        <div className="px-6 py-5 space-y-4 overflow-y-auto max-h-[70vh]">
          {/* Name */}
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-1">Event Name *</label>
            <input
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#0B0B0F]"
              value={form.name || ""}
              onChange={(e) => handle("name", e.target.value)}
              placeholder="e.g. Manufacturing Tech Summit"
            />
          </div>
          {/* Activity type */}
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-1">Activity Type *</label>
            <select
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#0B0B0F]"
              value={form.activityType || ACTIVITY_TYPES[0]}
              onChange={(e) => handle("activityType", e.target.value)}
            >
              {ACTIVITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          {/* Dates */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-1">Start Date *</label>
              <input
                type="date"
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#0B0B0F]"
                value={form.startDate || ""}
                onChange={(e) => handle("startDate", e.target.value)}
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-1">End Date</label>
              <input
                type="date"
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#0B0B0F]"
                value={form.endDate || ""}
                onChange={(e) => handle("endDate", e.target.value)}
              />
            </div>
          </div>
          {/* Location */}
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-1">Location</label>
            <input
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#0B0B0F]"
              value={form.location || ""}
              onChange={(e) => handle("location", e.target.value)}
              placeholder="City, State / Virtual"
            />
          </div>
          {/* Cost */}
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-1">Cost ($)</label>
            <input
              type="number"
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#0B0B0F]"
              value={form.cost || ""}
              onChange={(e) => handle("cost", Number(e.target.value) || 0)}
              placeholder="0"
            />
          </div>
          {/* Target audience */}
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-1">Target Audience Description</label>
            <textarea
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#0B0B0F] resize-none"
              rows={2}
              value={form.targetAudience || ""}
              onChange={(e) => handle("targetAudience", e.target.value)}
              placeholder="e.g. VP Ops and Plant Directors at Tier 1 automotive suppliers"
            />
          </div>
          {/* Status */}
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-1">Status</label>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => handle("status", s)}
                  className="px-3 py-1 rounded-full text-xs font-semibold border transition-all"
                  style={{
                    background: form.status === s ? statusColor(s as MarketingEvent["status"]) : "white",
                    color: form.status === s ? "white" : "#6B6B70",
                    borderColor: form.status === s ? statusColor(s as MarketingEvent["status"]) : "#E5E5E7",
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-neutral-100 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-neutral-600 border border-neutral-200 hover:bg-neutral-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            className="px-5 py-2 rounded-lg text-sm font-bold text-white transition-all hover:opacity-90"
            style={{ background: MI6_RED }}
          >
            {isEdit ? "Update Event" : "Add Event"}
          </button>
        </div>
      </div>
    </div>
  );
}

type ViewTab = "plan" | "actual" | "variance";
type CategoryFilter = "all" | "campaigns" | "webinars" | "content" | "events" | "paid";

const CATEGORY_MAP: Record<CategoryFilter, string[]> = {
  all:        [],
  campaigns:  ["Email Campaigns", "Direct Mail Campaigns", "Co-Marketing Partnerships"],
  webinars:   ["Webinars / Web Events"],
  content:    ["Sponsored Content", "Podcast Appearances"],
  events:     ["Trade Shows", "Hosted Round Tables", "In-Person Hosted Events", "Speaking Engagements (team as speakers)"],
  paid:       ["Paid Ads / Digital Media"],
};

export default function MarketingCalendar() {
  const [events, setEvents] = useState<MarketingEvent[]>([]);
  const [activeTab, setActiveTab] = useState<ViewTab>("plan");
  const [category, setCategory] = useState<CategoryFilter>("all");
  const [modal, setModal] = useState<{ open: boolean; event: Partial<MarketingEvent> | null }>({ open: false, event: null });
  const [detailEvent, setDetailEvent] = useState<MarketingEvent | null>(null);

  useEffect(() => {
    setEvents(seedEventsIfEmpty());
  }, []);

  // Esc and click-outside dismissal for the detail panel
  useEffect(() => {
    if (!detailEvent) return;
    function handleEsc(e: KeyboardEvent) {
      if (e.key === "Escape") setDetailEvent(null);
    }
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [detailEvent]);

  function handleSave(ev: MarketingEvent) {
    setEvents((prev) => {
      const exists = prev.find((e) => e.id === ev.id);
      const next = exists ? prev.map((e) => e.id === ev.id ? ev : e) : [...prev, ev];
      saveEvents(next);
      return next;
    });
    setModal({ open: false, event: null });
  }

  // Group events by month
  const byMonth: Record<number, MarketingEvent[]> = {};
  for (let m = 0; m < 12; m++) byMonth[m] = [];
  events.forEach((ev) => {
    eventMonths(ev).forEach((m) => {
      if (!byMonth[m].includes(ev)) byMonth[m].push(ev);
    });
  });

  // Filter by view tab
  function filterByTab(evs: MarketingEvent[]): MarketingEvent[] {
    let pool = evs;
    if (category !== "all") {
      const allowed = new Set(CATEGORY_MAP[category]);
      pool = pool.filter((e) => allowed.has(e.activityType));
    }
    if (activeTab === "plan") return pool.filter((e) => e.status !== "Cancelled");
    if (activeTab === "actual") return pool.filter((e) => e.status === "Completed" || e.actual);
    return pool; // variance: all
  }

  // ── Sidebar derived data ────────────────────────────────────────────────────
  const NOW_TS = Date.now();
  const ONE_WEEK = 7 * 24 * 60 * 60 * 1000;
  const filteredAll = filterByTab(events);
  const thisWeek = filteredAll
    .filter((e) => {
      const t = new Date(e.startDate).getTime();
      return t >= NOW_TS && t <= NOW_TS + ONE_WEEK;
    })
    .sort((a, b) => +new Date(a.startDate) - +new Date(b.startDate))
    .slice(0, 5);
  const nextBigLaunch = filteredAll
    .filter((e) => +new Date(e.startDate) >= NOW_TS && (e.cost ?? 0) >= 5000)
    .sort((a, b) => +new Date(a.startDate) - +new Date(b.startDate))[0];
  const nextLaunchDays = nextBigLaunch
    ? Math.max(0, Math.round((+new Date(nextBigLaunch.startDate) - NOW_TS) / (24 * 60 * 60 * 1000)))
    : null;

  const TAB_LABELS: Record<ViewTab, string> = {
    plan: "Plan View",
    actual: "Actual View",
    variance: "Variance View",
  };

  return (
    <MarketingShell>
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 shadow-sm">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[9px] uppercase tracking-[0.24em] text-neutral-500 mb-0.5">Marketing Activity Plan</div>
            <h1 className="text-lg font-bold text-neutral-900">Annual Activity Calendar — 2026</h1>
          </div>
          <div className="flex items-center gap-3">
            {/* View tabs */}
            <div className="flex gap-1 bg-neutral-100 rounded-lg p-0.5">
              {(Object.keys(TAB_LABELS) as ViewTab[]).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className="px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-150"
                  style={{
                    background: activeTab === tab ? MI6_BLACK : "transparent",
                    color: activeTab === tab ? "white" : "#6B6B70",
                  }}
                >
                  {TAB_LABELS[tab]}
                </button>
              ))}
            </div>
            <button
              onClick={() => setModal({ open: true, event: null })}
              className="px-4 py-2 rounded-lg text-sm font-bold text-white transition-all hover:opacity-90"
              style={{ background: MI6_RED }}
            >
              + Add Event
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 flex-1 w-full grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_300px] gap-6">
       <div className="min-w-0">

        {/* Filter pills */}
        <div className="mb-3 flex flex-wrap gap-1.5">
          {([
            { id: "all" as CategoryFilter,        label: "All" },
            { id: "campaigns" as CategoryFilter,  label: "Campaigns" },
            { id: "webinars" as CategoryFilter,   label: "Webinars" },
            { id: "content" as CategoryFilter,    label: "Content" },
            { id: "events" as CategoryFilter,     label: "Events" },
            { id: "paid" as CategoryFilter,       label: "Paid" },
          ]).map((p) => {
            const a = category === p.id;
            return (
              <button
                key={p.id}
                onClick={() => setCategory(p.id)}
                className="text-[11px] font-bold uppercase tracking-[0.08em] px-3 py-1 rounded-full border transition-colors"
                style={{
                  background: a ? MI6_BLACK : "white",
                  color:      a ? "white"    : "#525258",
                  borderColor: a ? MI6_BLACK : "#E5E5E7",
                }}
              >
                {p.label}
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="mb-4 flex flex-wrap gap-2">
          {ACTIVITY_TYPES.slice(0, 8).map((t) => {
            const s = CHIP_STYLE[t] || { bg: "#6B7280", text: "#fff" };
            return (
              <div key={t} className="flex items-center gap-1.5">
                <div
                  className="w-3 h-3 rounded-sm flex-shrink-0"
                  style={{ background: s.bg, border: s.border ? `1px solid ${s.border}` : "none" }}
                />
                <span className="text-[9px] text-neutral-500">{t}</span>
              </div>
            );
          })}
        </div>

        {/* Calendar grid — 12 columns (months) */}
        <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" }}>
        <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(12, minmax(80px, 1fr))", minWidth: "960px" }}>
          {MONTHS.map((month, mi) => {
            const monthEvents = filterByTab(byMonth[mi] || []);
            return (
              <div
                key={month}
                className="rounded-xl border bg-white overflow-hidden"
                style={{ borderColor: "#E5E5E7", minHeight: "180px" }}
              >
                {/* Month header */}
                <div
                  className="px-2 py-1.5 text-center"
                  style={{ background: MI6_BLACK, color: "white" }}
                >
                  <div className="text-[10px] font-bold uppercase tracking-[0.15em]">{month}</div>
                </div>
                {/* Events */}
                <div className="p-1.5 space-y-0.5">
                  {monthEvents.length === 0 ? (
                    <div className="text-[9px] text-neutral-300 text-center py-2">—</div>
                  ) : (
                    monthEvents.map((ev) => (
                      <EventChip
                        key={ev.id}
                        event={ev}
                        onClick={() => setDetailEvent(ev)}
                      />
                    ))
                  )}
                  {/* Variance view: show actual vs planned */}
                  {activeTab === "variance" && monthEvents.length > 0 && (
                    <div className="mt-1 pt-1 border-t border-neutral-100">
                      <div className="text-[8px] text-neutral-500 uppercase tracking-wide">
                        {monthEvents.filter((e) => e.status === "Completed").length}/{monthEvents.length} complete
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        </div>

        {/* Event list below calendar */}
        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 font-bold mb-3">All Events — {activeTab === "plan" ? "Committed Plan" : activeTab === "actual" ? "Actual / Logged" : "Full View"}</div>
          <div className="space-y-2">
            {filterByTab(events).map((ev) => (
              <div
                key={ev.id}
                className="bg-white rounded-xl border border-neutral-200 px-4 py-3 flex items-center justify-between gap-4 cursor-pointer hover:border-neutral-300 transition-colors"
                onClick={() => setDetailEvent(ev)}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className="w-2 h-8 rounded-full flex-shrink-0"
                    style={{ background: CHIP_STYLE[ev.activityType]?.bg || MI6_BLACK }}
                  />
                  <div className="min-w-0">
                    <div className="font-semibold text-neutral-800 text-sm truncate">{ev.name}</div>
                    <div className="text-xs text-neutral-500">
                      {ev.activityType} · {new Date(ev.startDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      {ev.endDate ? `–${new Date(ev.endDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""}
                      {ev.location ? ` · ${ev.location}` : ""}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {ev.cost && <span className="text-xs text-neutral-500">${ev.cost.toLocaleString()}</span>}
                  <span
                    className="px-2 py-0.5 rounded-full text-[10px] font-semibold"
                    style={{ background: statusColor(ev.status) + "22", color: statusColor(ev.status) }}
                  >
                    {ev.status}
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); setModal({ open: true, event: ev }); }}
                    className="text-xs text-neutral-500 hover:text-neutral-700 px-2 py-1 rounded border border-neutral-200 hover:border-neutral-300 transition-colors"
                  >
                    Edit
                  </button>
                </div>
              </div>
            ))}
            {filterByTab(events).length === 0 && (
              <div className="text-center text-neutral-500 text-sm py-8">No events in this view. Click "+ Add Event" to get started.</div>
            )}
          </div>
        </div>
       </div>

        {/* ── Sidebar: This week + Next big launch ──────────────────────── */}
        <aside className="hidden xl:block sticky top-32 self-start space-y-4">
          <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-neutral-100">
              <div className="text-[9px] uppercase tracking-[0.22em] text-neutral-500">This week</div>
              <div className="text-sm font-bold text-neutral-800">Upcoming events</div>
            </div>
            <div className="divide-y divide-neutral-100">
              {thisWeek.length === 0 ? (
                <div className="px-4 py-6 text-xs text-neutral-500 text-center">No events in the next 7 days.</div>
              ) : thisWeek.map((ev) => (
                <button
                  key={ev.id}
                  onClick={() => setDetailEvent(ev)}
                  className="w-full text-left px-4 py-3 hover:bg-neutral-50 transition-colors"
                >
                  <div className="text-[11px] font-semibold text-neutral-800 truncate">{ev.name}</div>
                  <div className="text-[10px] text-neutral-500 mt-0.5">
                    {new Date(ev.startDate).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                    {ev.location ? ` · ${ev.location}` : ""}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {nextBigLaunch && (
            <div
              className="rounded-xl shadow-sm overflow-hidden text-white"
              style={{ background: MI6_BLACK }}
            >
              <div className="px-4 py-3">
                <div className="text-[9px] uppercase tracking-[0.22em] text-neutral-300">Next big launch</div>
                <div className="text-sm font-bold mt-0.5 truncate">{nextBigLaunch.name}</div>
              </div>
              <div className="px-4 pb-4">
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-extrabold tabular-nums" style={{ color: "#FCA5A5" }}>{nextLaunchDays}</span>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-neutral-300">days out</span>
                </div>
                <div className="text-[11px] text-neutral-300 mt-2">
                  {nextBigLaunch.activityType} · {new Date(nextBigLaunch.startDate).toLocaleDateString("en-US", { month: "long", day: "numeric" })}
                </div>
                <button
                  onClick={() => setDetailEvent(nextBigLaunch)}
                  className="mt-3 w-full text-[11px] font-bold uppercase tracking-wider px-3 py-2 rounded text-white"
                  style={{ background: MI6_RED }}
                >
                  Open event
                </button>
              </div>
            </div>
          )}
        </aside>
      </main>

      {/* Event detail panel */}
      {detailEvent && (
        <div className="fixed inset-0 z-[60] flex items-center justify-end bg-black/40 backdrop-blur-sm" onClick={() => setDetailEvent(null)}>
          <div
            className="bg-white h-full w-full max-w-md shadow-2xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 flex items-center justify-between" style={{ background: MI6_BLACK }}>
              <div className="text-white font-bold text-base truncate">{detailEvent.name}</div>
              <button onClick={() => setDetailEvent(null)} className="text-neutral-300 hover:text-white text-xl leading-none ml-4">×</button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[9px] uppercase tracking-[0.18em] text-neutral-500 mb-0.5">Type</div>
                  <div className="text-sm font-medium text-neutral-700">{detailEvent.activityType}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-[0.18em] text-neutral-500 mb-0.5">Status</div>
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: statusColor(detailEvent.status) + "22", color: statusColor(detailEvent.status) }}>{detailEvent.status}</span>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-[0.18em] text-neutral-500 mb-0.5">Date(s)</div>
                  <div className="text-sm text-neutral-700">
                    {new Date(detailEvent.startDate).toLocaleDateString("en-US", { month: "long", day: "numeric" })}
                    {detailEvent.endDate ? `–${new Date(detailEvent.endDate).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}` : `, ${new Date(detailEvent.startDate).getFullYear()}`}
                  </div>
                </div>
                {detailEvent.location && (
                  <div>
                    <div className="text-[9px] uppercase tracking-[0.18em] text-neutral-500 mb-0.5">Location</div>
                    <div className="text-sm text-neutral-700">{detailEvent.location}</div>
                  </div>
                )}
                {detailEvent.cost && (
                  <div>
                    <div className="text-[9px] uppercase tracking-[0.18em] text-neutral-500 mb-0.5">Budget</div>
                    <div className="text-sm text-neutral-700">${detailEvent.cost.toLocaleString()}</div>
                  </div>
                )}
              </div>
              {detailEvent.targetAudience && (
                <div>
                  <div className="text-[9px] uppercase tracking-[0.18em] text-neutral-500 mb-1">Target Audience</div>
                  <div className="text-sm text-neutral-600">{detailEvent.targetAudience}</div>
                </div>
              )}
              {detailEvent.actual && (
                <div className="rounded-lg border border-neutral-200 p-3">
                  <div className="text-[9px] uppercase tracking-[0.18em] text-neutral-500 mb-2">Actual Results</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {detailEvent.actual.mqlsAdded ? <div><span className="text-neutral-500">MQLs: </span><span className="font-bold" style={{ color: MI6_BLACK }}>{detailEvent.actual.mqlsAdded}</span></div> : null}
                    {detailEvent.actual.followUpMeetingsBooked ? <div><span className="text-neutral-500">Meetings: </span><span className="font-bold" style={{ color: MI6_BLACK }}>{detailEvent.actual.followUpMeetingsBooked}</span></div> : null}
                    {detailEvent.actual.costActual ? <div><span className="text-neutral-500">Actual Cost: </span><span className="font-bold">${detailEvent.actual.costActual.toLocaleString()}</span></div> : null}
                    {detailEvent.actual.notes && <div className="col-span-2 text-neutral-500">{detailEvent.actual.notes}</div>}
                  </div>
                </div>
              )}
              {detailEvent.hooks && (
                <div>
                  <div className="text-[9px] uppercase tracking-[0.18em] text-neutral-500 mb-2">Sales Hooks</div>
                  <div className="space-y-1">
                    {(["invite","participation","round_table"] as const).map((hk) => {
                      const hook = detailEvent.hooks![hk];
                      return (
                        <div key={hk} className="flex items-center gap-2">
                          <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${hook.approved ? "bg-emerald-500" : "bg-neutral-300"}`} />
                          <span className="text-xs text-neutral-500 capitalize">{hk.replace("_"," ")} hook: {hook.approved ? <span className="text-emerald-600 font-semibold">Approved</span> : <span className="text-neutral-500">Pending</span>}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => { setDetailEvent(null); setModal({ open: true, event: detailEvent }); }}
                  className="flex-1 px-3 py-2 rounded-lg text-sm border border-neutral-200 text-neutral-600 hover:bg-neutral-50 transition-colors"
                >
                  Edit Event
                </button>
                <a
                  href={`#/log/marketing/event/${detailEvent.id}`}
                  className="flex-1 px-3 py-2 rounded-lg text-sm font-bold text-white text-center transition-all hover:opacity-90"
                  style={{ background: MI6_RED }}
                >
                  Log Results
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/edit modal */}
      {modal.open && (
        <EventModal
          event={modal.event}
          onClose={() => setModal({ open: false, event: null })}
          onSave={handleSave}
        />
      )}
    </MarketingShell>
  );
}
